/* ---------------------------------------------------------------------------
   The live-load library: what one wheel line of each vehicle demands of a
   simple span.

   ONE TABLE, NOT SIXTEEN. Every workbook in the program carries its own copy of
   these formulas — DFDLLL for the rating vehicle, POSTLL again for the posting
   vehicles, in all sixteen files — and they are identical every time. They are
   MLRB Appendix III's closed forms: the envelope of the moment and shear a
   standard truck can produce on a simple span, evaluated per wheel line, as a
   piecewise function of span alone. Reduced to one table here (correction C6).

   PER WHEEL LINE, NOT PER LANE, AND WITHOUT IMPACT. What comes out of here is
   one half of one truck on one notional line of wheels. Getting from that to a
   member's demand takes a distribution factor and an impact factor, which is
   demand.ts. Nothing in this file knows what it is being applied to.

   THE ROUNDING IS PART OF THE ANSWER. The workbook rounds each lookup to two
   decimals (`ROUND(...,2)`) and applies the distribution factor to the ROUNDED
   value, so the rounding propagates into the rating factor. Reproduced
   deliberately — see correction C10 — and available unrounded from
   `momentExact`/`shearExact` for anyone who needs to see the size of it.

   BANDS ARE HALF-OPEN, LOW END INCLUSIVE. The workbook writes each band as
   `IF(L < break, this, that)`, so a span exactly on a break takes the UPPER
   band. `L = 7.47` is the second MS18 band, not the first. This matters at
   exactly four span values per vehicle and is the kind of thing a rewrite gets
   wrong silently.
   --------------------------------------------------------------------------- */

import { excelRound } from './round.ts';

/**
 * The four vehicles CBPLR knows.
 *
 * `MS18` is the rating vehicle — AASHTO HS20-44, 33 t — and the other three are
 * the Philippine posting trucks. The ids are the workbook's own labels so a
 * value can be traced back to the sheet it came from; the display names are the
 * profile's business (see profile.ts), because another department posting under
 * another standard will have other trucks entirely.
 */
export type VehicleId = 'MS18' | 'Type1-1' | 'Type1-2' | 'Type12-2';

export const VEHICLE_IDS: readonly VehicleId[] = ['MS18', 'Type1-1', 'Type1-2', 'Type12-2'];

export function isVehicleId(v: string): v is VehicleId {
  return (VEHICLE_IDS as readonly string[]).includes(v);
}

/* --------------------------- the piecewise forms --------------------------- */

/**
 * One band of a piecewise envelope.
 *
 * `upTo` is the span at which this band STOPS applying, exclusive; the last band
 * in a list carries `Infinity`. `of` is the closed form, in kN-m per wheel line
 * for moment and kN per wheel line for shear, with L in metres.
 *
 * `text` is the formula as the workbook prints it, carried so the rating report
 * can show the reader which line of Appendix III produced their number. It is
 * documentation with a job, not a comment.
 */
interface Band {
  upTo: number;
  of: (L: number) => number;
  text: string;
}

function evaluate(bands: readonly Band[], L: number): { value: number; text: string } {
  for (const band of bands) {
    if (L < band.upTo) return { value: band.of(L), text: band.text };
  }
  /* Unreachable while every list ends at Infinity, which a test pins. Throwing
     rather than returning 0: a span that fell off the end of the envelope is a
     bug in this table, and a demand of zero would be reported as a rating. */
  throw new Error(`no live-load band covers a span of ${L} m`);
}

/** Half the axle spacing, the form every one of these envelopes is written in. */
function halfBack(L: number, spacing: number): number {
  return ((L - spacing) / 2) ** 2;
}

const MOMENT: Readonly<Record<VehicleId, readonly Band[]>> = {
  MS18: [
    { upTo: 7.47, of: (L) => (72 * L) / 4, text: '72L/4' },
    { upTo: 10.67, of: (L) => (144 / L) * halfBack(L, 2.134), text: '(144/L)[(L-2.134)/2]^2' },
    {
      upTo: Infinity,
      of: (L) => (162 / L) * halfBack(L, 1.422) - 76.81,
      text: '(162/L)[(L-1.422)/2]^2 - 76.81',
    },
  ],
  'Type1-1': [
    { upTo: 8.1, of: (L) => (66.8 * L) / 4, text: '66.8L/4' },
    /* 0.853 m, once. The printed reference table in the workbook writes this
       spacing as 0.8534 in one of the two factors — correction C5. */
    { upTo: Infinity, of: (L) => (83.5 / L) * halfBack(L, 0.853), text: '(83.5/L)[(L-0.853)/2]^2' },
  ],
  'Type1-2': [
    { upTo: 8.62, of: (L) => (90.1 / L) * halfBack(L, 0.6095), text: '(90.1/L)[(L-0.6095)/2]^2' },
    {
      upTo: Infinity,
      of: (L) => (132.5 / L) * halfBack(L, 1.049) - 54.92,
      text: '(132.5/L)[(L-1.049)/2]^2 - 54.92',
    },
  ],
  'Type12-2': [
    { upTo: 6.53, of: (L) => (80.3 / L) * halfBack(L, 0.6095), text: '(80.3/L)[(L-0.6095)/2]^2' },
    {
      upTo: 14.72,
      of: (L) => (106.2 / L) * halfBack(L, 0.357) - 48.94,
      text: '(106.2/L)[(L-0.357)/2]^2 - 48.94',
    },
    {
      upTo: Infinity,
      of: (L) => (186.5 / L) * halfBack(L, 2.252) - 167.36,
      text: '(186.5/L)[(L-2.252)/2]^2 - 167.36',
    },
  ],
};

const SHEAR: Readonly<Record<VehicleId, readonly Band[]>> = {
  MS18: [
    { upTo: 4.267, of: () => 72, text: '72' },
    { upTo: 8.534, of: (L) => 144 * (1 - 2.134 / L), text: '144(1 - 2.134/L)' },
    { upTo: Infinity, of: (L) => 162 * (1 - 2.845 / L), text: '162(1 - 2.845/L)' },
  ],
  'Type1-1': [
    { upTo: 4.267, of: () => 66.8, text: '66.8' },
    { upTo: Infinity, of: (L) => 83.5 * (1 - 0.853 / L), text: '83.5(1 - 0.853/L)' },
  ],
  'Type1-2': [
    { upTo: 5.791, of: (L) => 90.1 * (1 - 0.6095 / L), text: '90.1(1 - 0.6095/L)' },
    { upTo: Infinity, of: (L) => 132.5 * (1 - 2.268 / L), text: '132.5(1 - 2.268/L)' },
  ],
  'Type12-2': [
    { upTo: 4.572, of: (L) => 80.3 * (1 - 0.6095 / L), text: '80.3(1 - 0.6095/L)' },
    { upTo: 12.497, of: (L) => 106.2 * (1 - 1.576 / L), text: '106.2(1 - 1.576/L)' },
    { upTo: Infinity, of: (L) => 186.5 * (1 - 5.673 / L), text: '186.5(1 - 5.673/L)' },
  ],
};

/* --------------------------- the public reads --------------------------- */

/** A demand with the Appendix III line that produced it, for the report. */
export interface LiveLoadEffect {
  /** kN-m or kN per wheel line, rounded as the workbook rounds it. */
  value: number;
  /** The same, unrounded. The size of correction C10, on this span. */
  exact: number;
  /** The closed form, as the manual prints it. */
  formula: string;
}

/**
 * Maximum live-load moment per wheel line, kN-m, no impact.
 *
 * `span` in metres. Reproduces `DFDLLL!I43:I56`.
 */
export function liveMoment(vehicle: VehicleId, span: number): LiveLoadEffect {
  const { value, text } = evaluate(MOMENT[vehicle], span);
  return { value: excelRound(value, 2), exact: value, formula: text };
}

/**
 * Maximum live-load shear per wheel line, kN, no impact.
 *
 * At a distance d from the support, as the workbook's own row label says — the
 * envelope already accounts for it. `DFDLLL!I60:I73`.
 */
export function liveShear(vehicle: VehicleId, span: number): LiveLoadEffect {
  const { value, text } = evaluate(SHEAR[vehicle], span);
  return { value: excelRound(value, 2), exact: value, formula: text };
}

/* --------------------------- impact --------------------------- */

/**
 * The impact fraction, capped.
 *
 * `I = 15.24/(L + 38)`, not more than 0.30 — AASHTO's rule in metres, and the
 * cap binds for every span under about 12.8 m, which is most of the register.
 * Returned as a fraction, so a caller writes `(1 + impactFor(L))`.
 *
 * IN THE PROFILE, NOT HERE, would be wrong. The 15.24 and the 38 are the
 * definition of impact under this standard rather than a departmental choice; a
 * department that used a different rule would be running a different method, and
 * that is a new module, not a configured number.
 */
export const IMPACT_CAP = 0.3;

export function impactFor(span: number): number {
  return Math.min(IMPACT_CAP, 15.24 / (span + 38));
}
